/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Formily decorator that wraps FormItem with a remove button
 */
import _ from 'lodash';
import { DeleteOne } from '@icon-park/react';
import { FormItem } from '@formily/antd-v5';
import { useField, useForm } from '@formily/react';

import { OPTIONAL_PATIENT_FIELDS_CONFIG } from 'app-constants/eligibility';

const RemovableFormItem: React.FC<React.PropsWithChildren<EligibilityBatchUpload.IRemovableFormItemProps>> = ({
	onFieldRemove,
	...props
}) => {
	const form = useForm();
	const field = useField();
	const fieldKey = field.props.name as EligibilityBatchUpload.IOptionalPatientFieldKey;
	const fieldConfig = _.find(OPTIONAL_PATIENT_FIELDS_CONFIG, { key: fieldKey });

	const handleRemove = () => {
		form.setValuesIn(fieldKey, undefined);
		form.setFieldState(fieldKey, { display: 'none' });
		onFieldRemove?.(fieldKey);
	};

	const wrappedChildren = (
		<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
			<div style={{ flex: 1 }}>{props.children}</div>
			<span
				role='button'
				tabIndex={0}
				onClick={handleRemove}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						handleRemove();
					}
				}}
				aria-label={`Remove ${fieldConfig?.label ?? fieldKey}`}
				data-testid={`remove-optional-field-${fieldKey}`}
				className='optional-field-remove-button'
			>
				<DeleteOne size='16' />
			</span>
		</div>
	);

	return (
		<div data-testid={`optional-field-row-${fieldKey}`}>
			<FormItem {...props}>{wrappedChildren}</FormItem>
		</div>
	);
};

export default RemovableFormItem;
